/**
 * Deterministic rules for business expense (tax-time) data notices and informational findings.
 */

import { differenceInCalendarDays, parseISO } from 'date-fns';
import type {
  FinancialSummaryDataNotice,
  FinancialSummaryRuleFinding,
  ReportTransactionRow,
} from '../../types/report.types';

export interface BusinessExpenseRulesInput {
  transactions: ReportTransactionRow[];
  periodEndDate: string;
  gstRegistered: boolean;
  currentAiVendorMonthlySpendAud?: number;
}

const AMOUNT_TOLERANCE_RATIO = 0.05;
const DUPLICATE_WINDOW_DAYS = 5;

function expenseAud(tx: ReportTransactionRow): number {
  return tx.expenseAmountAud ?? Math.abs(tx.amount);
}

function amountsSimilar(a: number, b: number): boolean {
  const max = Math.max(a, b, 1);
  return Math.abs(a - b) / max <= AMOUNT_TOLERANCE_RATIO;
}

export function detectDuplicateCharges(
  transactions: ReportTransactionRow[]
): FinancialSummaryRuleFinding[] {
  const findings: FinancialSummaryRuleFinding[] = [];
  const byVendor = new Map<string, ReportTransactionRow[]>();
  for (const tx of transactions) {
    const key = tx.vendorLabel ?? tx.description;
    const list = byVendor.get(key) ?? [];
    list.push(tx);
    byVendor.set(key, list);
  }

  for (const [vendor, rows] of byVendor) {
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    for (let i = 0; i < sorted.length; i += 1) {
      for (let j = i + 1; j < sorted.length; j += 1) {
        const a = sorted[i];
        const b = sorted[j];
        const days = Math.abs(
          differenceInCalendarDays(parseISO(a.date), parseISO(b.date))
        );
        if (days > DUPLICATE_WINDOW_DAYS) break;
        if (!amountsSimilar(expenseAud(a), expenseAud(b))) continue;
        findings.push({
          id: `duplicateCharge:${vendor}:${a.date}:${b.date}`,
          severity: 'medium',
          amount: expenseAud(a) + expenseAud(b),
          message: `${vendor}: two similar charges (${formatAud(expenseAud(a))} and ${formatAud(expenseAud(b))}) within ${days} day(s) on ${a.date} and ${b.date}. May be a duplicate subscription or double billing.`,
          action: 'Compare against vendor invoices and cancel or claim only one charge if duplicated.',
        });
        break;
      }
    }
  }
  return findings;
}

function formatAud(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export function detectSubscriptionLifecycle(
  transactions: ReportTransactionRow[],
  periodEndDate: string
): FinancialSummaryRuleFinding[] {
  const findings: FinancialSummaryRuleFinding[] = [];
  const byVendor = new Map<string, ReportTransactionRow[]>();
  for (const tx of transactions) {
    const key = tx.vendorLabel ?? tx.description;
    const list = byVendor.get(key) ?? [];
    list.push(tx);
    byVendor.set(key, list);
  }

  const periodEnd = parseISO(periodEndDate);

  for (const [vendor, rows] of byVendor) {
    if (rows.length === 0) continue;
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const months = new Set(sorted.map((r) => r.date.slice(0, 7))).size;

    if (rows.length >= 3 && months >= 3) {
      const daysSinceLast = differenceInCalendarDays(periodEnd, parseISO(last.date));
      if (daysSinceLast >= 120) {
        findings.push({
          id: `subscriptionStopped:${vendor}`,
          severity: 'low',
          message: `${vendor}: last charge ${last.date}; no further charges in the ${daysSinceLast} days before period end. Subscription may have been cancelled.`,
          action: 'Keep cancellation confirmation for your records.',
        });
      }
    }

    if (rows.length === 1) {
      findings.push({
        id: `subscriptionStarted:${vendor}`,
        severity: 'low',
        message: `${vendor}: one charge in this period (${first.date}).`,
        action: 'Ensure business-use apportionment and receipts are set if applicable.',
      });
    }
  }

  return findings;
}

export function detectGstAndReceiptGaps(
  transactions: ReportTransactionRow[]
): FinancialSummaryDataNotice[] {
  const notices: FinancialSummaryDataNotice[] = [];
  const unknownGst = transactions.filter((t) => t.gstStatus === 'unknown');
  if (unknownGst.length) {
    const total = unknownGst.reduce((s, t) => s + expenseAud(t), 0);
    notices.push({
      id: 'gstUnknown',
      message: `${unknownGst.length} transaction(s) (${formatAud(total)}) have GST status unknown — common for overseas digital suppliers. Do not claim GST without valid tax invoices.`,
    });
  }

  const missingReceipt = transactions.filter((t) => !t.receiptAttached);
  if (missingReceipt.length) {
    notices.push({
      id: 'receiptMissing',
      message: `${missingReceipt.length} transaction(s) have no receipt marked on file. Accountants typically require tax invoices for deductions.`,
    });
  }

  const missingBusinessPct = transactions.filter((t) => t.businessUsePercent == null);
  if (missingBusinessPct.length) {
    notices.push({
      id: 'businessUseUnset',
      message: `${missingBusinessPct.length} transaction(s) have no business-use % set. Claimable amounts exclude these until apportioned.`,
    });
  }

  return notices;
}

export function evaluateBusinessExpenseRules(
  input: BusinessExpenseRulesInput
): FinancialSummaryRuleFinding[] {
  const findings: FinancialSummaryRuleFinding[] = [];
  findings.push(...detectDuplicateCharges(input.transactions));
  findings.push(...detectSubscriptionLifecycle(input.transactions, input.periodEndDate));

  if (input.currentAiVendorMonthlySpendAud != null && input.currentAiVendorMonthlySpendAud > 0) {
    findings.push({
      id: 'aiToolOverlap',
      severity: 'low',
      message: `Estimated ${formatAud(input.currentAiVendorMonthlySpendAud)}/month across AI-related tools in the latest month of this report (informational).`,
      action: 'Review overlap only if you are consolidating subscriptions.',
    });
  }

  return findings;
}

export const BUSINESS_EXPENSE_TAX_FIELDS_HELP_NOTICE: FinancialSummaryDataNotice = {
  id: 'taxFieldsHelp',
  message:
    'Business-use %: set in the Business Expense report wizard (step 2, Business claims) before you generate.\n' +
    'Claimable amounts use business use % times expense amount. Business name, ABN, and GST registered: Settings, Account, Business and tax reporting.',
};

export function businessExpenseDuplicateSummaryNotice(
  ruleFindings: FinancialSummaryRuleFinding[]
): FinancialSummaryDataNotice | null {
  const duplicateCount = ruleFindings.filter((f) => f.id.startsWith('duplicateCharge')).length;
  if (duplicateCount === 0) return null;
  return {
    id: 'duplicateChargeSummary',
    message: `${duplicateCount} possible duplicate charge pair(s) — see Informational context (before transaction appendix).`,
  };
}

export function businessExpenseDataNotices(
  ruleFindings: FinancialSummaryRuleFinding[],
  transactionNotices: FinancialSummaryDataNotice[]
): FinancialSummaryDataNotice[] {
  const duplicateSummary = businessExpenseDuplicateSummaryNotice(ruleFindings);
  const extras = [BUSINESS_EXPENSE_TAX_FIELDS_HELP_NOTICE];
  if (duplicateSummary) extras.unshift(duplicateSummary);
  return [...transactionNotices, ...extras];
}
